import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { EmployeePayrollPreview } from '@/services/payroll/payrollPreviewService';

interface PreviewEmployeeTableProps {
  employees: EmployeePayrollPreview[];
}

export const PreviewEmployeeTable = ({ employees }: PreviewEmployeeTableProps) => {
  const { currencyCode } = useWorkplaceCurrency();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (profileId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(profileId)) {
      newExpanded.delete(profileId);
    } else {
      newExpanded.add(profileId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(2);
  };

  return (
    <ScrollArea className="h-[400px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Employee</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead className="text-right">Paid Leave</TableHead>
            <TableHead className="text-right">Gross Pay</TableHead>
            <TableHead className="text-right">Deductions</TableHead>
            <TableHead className="text-right">Net Pay</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map((employee) => {
            const isExpanded = expandedRows.has(employee.profile_id);
            return (
              <React.Fragment key={employee.profile_id}>
                <TableRow
                  className="cursor-pointer hover:bg-accent/50"
                  onClick={() => toggleRow(employee.profile_id)}
                >
                  <TableCell>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {employee.first_name[0]}
                          {employee.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {employee.first_name} {employee.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{employee.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatHours(employee.total_hours)}
                  </TableCell>
                  <TableCell className="text-right">
                    {employee.paid_leave_hours > 0 ? (
                      <span className="text-blue-600">{formatHours(employee.paid_leave_hours)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(employee.gross_pay)}
                  </TableCell>
                  <TableCell className="text-right text-red-600">
                    -{formatCurrency(employee.total_deductions)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-green-600">
                    {formatCurrency(employee.net_pay)}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow key={`${employee.profile_id}-expanded`}>
                    <TableCell colSpan={7} className="bg-muted/50">
                      <div className="py-4 px-6 space-y-3">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="secondary">{employee.role}</Badge>
                          <span className="text-sm text-muted-foreground">
                            Regular: {formatHours(employee.regular_hours)} | Overtime:{' '}
                            {formatHours(employee.overtime_hours)} | Holiday:{' '}
                            {formatHours(employee.holiday_hours)}
                            {employee.paid_leave_hours > 0 && (
                              <> | <span className="text-blue-600">Paid Leave: {formatHours(employee.paid_leave_hours)}</span></>
                            )}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-semibold">Line Items:</p>
                          {employee.line_items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-sm py-2 px-3 bg-background rounded"
                            >
                              <div className="flex-1">
                                <span className="font-medium">{item.description}</span>
                                {item.hours > 0 && (
                                  <span className="text-muted-foreground ml-2">
                                    ({formatHours(item.hours)} hrs @ {formatCurrency(item.rate)}/hr)
                                  </span>
                                )}
                                {item.isServiceCharge && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    Non-Taxable
                                  </Badge>
                                )}
                              </div>
                              <span className="font-medium">{formatCurrency(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};
